import {AppApi} from 'store/customer/api';
import {ExtraVariation, Job} from 'components/customer/Job/entity/Job';
import {
    JobCabinetVariation,
    JobCabinetVariationApi,
} from 'components/customer/Job/entity/JobCabinetVariation';
import {mapVariation} from 'components/customer/Job/helpers/mapVariation';
import {
    FingerPullProduct,
    FingerPullProductApi,
} from 'components/customer/Job/entity/FingerPull';
import {mapFingerPull} from 'components/customer/Job/helpers/mapFingerPull';
import {DashboardJob} from 'components/customer/Job/entity/DashboardJob';
import {RoomResponse} from 'components/customer/Product/entity/Room';
import {RoomPlannerLayout} from 'components/customer/RoomPlanner/types';
import HardwareOption from 'IndexedDB/models/HardwareOption';

export type MinimumUsageProduct = {
    id: number;
    name: string;
    roomName: string | null;
    roomNumber: number | undefined;
};

export type MinimumUsage = {
    materialType: string;
    id: number;
    jobId: number;
    jobCabinetIds: string;
    benchtopIds: string | null;
    materialId: number;
    usage: number;
    calculatedSheet: number;
    rolloverUsage: number;
    minimumUsage: number;
    areaCost: number;
    type: string | null;
    doubleSidedCost: number;
    cost: number;
    measurement: string;
    products: MinimumUsageProduct[];
    lengthCost: number;
    edgeId: number;
};

export interface DiscountItem {
    name: string;
    cost: number;
}

export interface BreakdownItem {
    name: string;
    cost: number;
    collapse?: boolean;
    items?: DiscountItem[];
}

type CouponDetail = {
    invalid: boolean;
    coupon_code: string;
    expiry_date: string;
    discount_free_shipping: number;
};

export type PackagingOptionCost = {
    id: number;
    name: string;
    calculation: string;
    cost: number;
    category: number;
    display_category: string;
    show_category: boolean;
    price_type: number;
    costing_method: number;
    needs_confirming: boolean;
    has_price: boolean;
};

export type JobCost = {
    customerMinCharge: number;
    totalVariationCost: number;
    customerDiscount: string;
    supplierDiscount: number;
    freightCost: number;
    jobSubtotal: number;
    tax: number;
    taxOnlyRate: number;
    totalJobCostExclTax: number;
    totalJobCost: number;
    preliminaryJobCost: number;
    preliminaryTax: number;
    preliminaryJobCostInclTax: number;
    totalMinUsage: number;
    supplierCommision: number;
    payPalSurcharge: number;
    maxJobSubtotal: number;
    coupon: number;
    couponDetails: CouponDetail;
    pricingMessages?: string[];
    pricingBreakdown?: BreakdownItem[];
    gfp_markup: number;
    benchtopMaterialUsage: MinimumUsage[];
    cabinetMaterialUsage: MinimumUsage[];
    additionServicesCost: number;
    packagingOptions: PackagingOptionCost[];
    packagingOptionsCost: number;
    defaultFreightApplies: boolean;
    hasIndicativeDeliveryExtras: boolean;
    missingRequiredDeliveryExtraGroups: string[];
    jobMinChargeFromCustMin: number;
    supplierCommission: number;
    currencyType?: string;
    jobBenchtopsCost?: number;
    sundryCosts?: number;
    jobExtraVariationCost?: number;
};

interface JobResponse {
    displayId: number;
    jobId: number;
    messages: string[];
}

export interface JobRoom {
    RoomNumber: number;
    RoomName: string;
    RoomDescription: string;
    CompanyLabel: string;
    qrUrl: string;
    InteriorMaterial: string;
    ExteriorMaterial: string;
    InteriorEdging: string;
    ExteriorEdging: string;
    DoorDwrProductName: string;
    HingeSeries: string;
    DrawerSystem: string;
    ToeKickHeight: string;
    JobCabinets: JobCabinet[];
}

export interface RoomPreview {
    RoomResponse: RoomResponse;
    layout: RoomPlannerLayout;
    hardware_options: HardwareOption[];
    product_info: JobCabinet[];
}

export interface DrawerDetail {
    MaterialName: string;
    DrawerPrefix: string;
    DrawerFaceHeight: string;
    DrawerRunnerHeight: string;
    DrawerRunnerDepth: string;
    DrawerPositionToBottom: string;
}

export interface DrawerEdge {
    top: string;
    bottom: string;
    left: string;
    right: string;
}

export interface JobCabinet {
    '3DRoomURL': string;
    RoomProductNo: string;
    CabinetNumber: string;
    Slug?: string;
    Type: string;
    ProcessName: string;
    ProductDescription: string;
    Quantity: string;
    ProductHeight: string;
    ProductWidth: string;
    ProductLeftWidth?: string;
    ProductRightWidth?: string;
    ProductDepth: string;
    ProductLength: string;
    KickHeight: string;
    KickWidth: string;
    KickDepth: string;
    TopChoice: string;
    ExteriorMaterial: string;
    ExteriorEdging: string;
    InteriorMaterial: string;
    InteriorEdging: string;
    ExcludeHardware: string;
    AdjustableLegs: string;
    DoorAmount: string;
    VerticalClearanceBetweenDoors?: string;
    BaseTopClearance: string;
    BaseBottomClearance: string;
    BaseLeftSideClearance: string;
    BaseRightSideClearance: string;
    SpecificHingeHeights: string;
    HingeSeries: string;
    DoorDwrProductName: string;
    DoorHang: string;
    DoorImage: string;
    DrawerImage: string;
    Shelves: string;
    PartSetback: string;
    AssemblyNeeded: string;
    DrawerAmount: string;
    ClearanceBetweenDrawers: string;
    DrawerDetail: DrawerDetail[];
    DrawerEdging: DrawerEdge[];
    TallTopClearance: string;
    TallBottomClearance: string;
    TallLeftSideClearance: string;
    TallRightSideClearance: string;
    Image: string;
    ManuDesc: string;
    VariationRequest: string;
    VariationName: string;
    qrUrl: string;
    Comments: string;
}

export interface JobBenchtop {
    id: number;
    JobProductNo: string;
    Quantity: 1;
    Shape: string;
    Material: {
        'Material Type': string;
        Colour: string;
        Thickness: string;
    };
    PartCount: number;
    Dimensions: {
        A: number;
        B: number;
        C: number;
        D: number;
        E: number;
        F: number;
        G: number;
        H: number;
        I: number;
    };
    Joins: string[];
    ButtJoinDimensions: string[];
    ButtJoinName: string;
    Profiles: {
        A: string;
        B: string;
        C: string;
        D: string;
        E: string;
        F: string;
        G: string;
        H: string;
    };
    image: string;
    VariationRequest: string;
}

export interface JobSundry {
    HardwareNo: string;
    ItemName: string;
    ItemNo: number;
    Quantity: number;
    Brand: string;
    StockStatus: string;
    Fixing: string;
    'Height (mm)': string;
    imagePath: string;
}

export interface JobDeliveryExtra {
    id: number;
    name: string;
    description: string;
    category: string;
    showCategory: boolean;
}

export interface JobDetailsPreview {
    JobNumber: number;
    JobName: string;
    Status: string;
    Description: string;
    CustomerName: string;
    'Manufacturer Name': string;
    InvoiceTo: string;
    OrderDate: string;
    ExpectedDeliveryDate: string;
    DispatchMethod: string;
    ContactNumber: string;
    JobRooms: JobRoom[];
    jobBenchtops: JobBenchtop[];
    JobSundries: JobSundry[];
    jobDeliveryExtras: JobDeliveryExtra[];
    deliveryAddress: string;
    customerEmail: string;
    title: string;
    generatedDate: string;
    submittedDate: string;
    requestedDeliveryDate?: string | null;
    pdfLogo: string;
    tableOfContentsFirst?: boolean;
}

export interface Jobs {
    completedJobs: DashboardJob[];
    pendingApprovalJobs: DashboardJob[];
    pendingConfirmationJobs: DashboardJob[];
    pendingPaymentJobs: DashboardJob[];
    productionJobs: DashboardJob[];
    quotedJobs: DashboardJob[];
}

const JobApi = AppApi.injectEndpoints({
    endpoints: (builder) => ({
        getJobs: builder.query<Jobs, {limit: number}>({
            query: ({limit = 10}) => `jobs/dashboard?limit=${limit}`,
            transformResponse: (response: {data: Jobs}) => response.data,
            providesTags: ['Jobs'],
        }),
        getJobCost: builder.query<JobCost | null, {jobId: number}>({
            query: ({jobId}) => `job/${jobId}/cost`,
            transformResponse: (response: {cost: JobCost}) => response.cost,
            providesTags: (response, error, {jobId}) => [
                {type: 'JobCost', jobId},
            ],
        }),
        // NOTE: This api can probably be used to fetch cost and products list
        // without additional network request. Sorry Nav. I should have thought
        // about this sooner. Will create a tech debt ticket right after to
        // consolidate all job and job related network request and data as
        // Updating jobContext is probably out of context for this ticket.
        getJob: builder.query<Job, {jobId: number; timestamp?: number}>({
            query: ({jobId}) => `jobs/${jobId}`,
            providesTags: (response, error, {jobId}) => [{type: 'Job', jobId}],
            transformResponse: (response: {data: Job}) => {
                const job = response.data;

                if (Array.isArray(job.rooms)) {
                    job.rooms = job.rooms.map((room, index) => {
                        room.roomNumber = index + 1;
                        if (room.jobBenchtops) {
                            room.jobBenchtops.map((product, productIndex) => {
                                product.productNumber = `${index + 1}-${
                                    productIndex + 1
                                }`;
                            });
                        }

                        return room;
                    });
                }

                return job;
            },
        }),
        getJobStatusStatistics: builder.query<string[], void>({
            query: () => 'jobs/statistics',
            providesTags: ['JobStatus'],
            transformResponse: (response: {data: string[]}) => response.data,
        }),
        jobReset: builder.mutation<void, {jobId: number; isReset?: boolean}>({
            query: ({jobId, isReset = true}) => ({
                url: `jobs/${jobId}/back-to-processing?isReset=${String(
                    isReset
                )}`,
                method: 'POST',
            }),
            invalidatesTags: [
                'Job',
                'Variations',
                'BTMBenches',
                'JobCost',
                'JobCabinetVariationDetails',
            ],
        }),
        confirmVariations: builder.mutation<
            void,
            {
                variations: {
                    job_cabinet_variations?: number[];
                    btm_variations?: number[];
                    extra_variations?: number[];
                };
                jobId: number;
            }
        >({
            query: ({jobId, variations}) => ({
                url: `jobs/${jobId}/confirmVariations`,
                method: 'POST',
                body: variations,
            }),
            invalidatesTags: [
                'Variations',
                'JobCabinetVariationDetails',
                'BTMBenches',
                'ProductRoom',
            ],
        }),
        getExtraVariations: builder.query<ExtraVariation[], {jobId: number}>({
            query: ({jobId}) => `/job/extra-variations/${jobId}`,
            transformResponse: (response: {data: ExtraVariation[]}) => {
                return response.data;
            },
            providesTags: () => [{type: 'Variations'}],
        }),
        deleteAllVariations: builder.mutation<void, {jobId: number}>({
            query: ({jobId}) => ({
                url: `jobs/${jobId}/deleteVariations`,
                method: 'DELETE',
            }),
            invalidatesTags: [
                'Variations',
                'JobCabinetVariationDetails',
                'BTMBenches',
                'ProductRoom',
            ],
        }),
        updateRequestedDeliveryDate: builder.mutation<
            void,
            {jobId: number; requestedDeliveryDate: string}
        >({
            query: ({jobId, requestedDeliveryDate}) => ({
                url: `jobs/${jobId}/requested-delivery-date`,
                method: 'PUT',
                body: {
                    requestedDeliveryDate,
                },
            }),
            invalidatesTags: ['Job'],
        }),
        getJobCabinetVariationDetails: builder.query<
            JobCabinetVariation[],
            {jobId: number}
        >({
            query: ({jobId}) => `job/${jobId}/job-cabinet-variation-details`,
            transformResponse: (response: {
                variations: JobCabinetVariationApi[];
            }) => response.variations.map(mapVariation),
            providesTags: (response, error, {jobId}) => [
                {type: 'JobCabinetVariationDetails', jobId},
            ],
        }),
        getFingerPullRecessedRails: builder.query<
            FingerPullProduct[],
            {jobId: number}
        >({
            query: ({jobId}) =>
                `jobs/${jobId}/get-finger-pull-and-recessed-rails`,
            transformResponse: (response: {
                job_cabinets: FingerPullProductApi[];
            }) => {
                return response.job_cabinets.map(mapFingerPull);
            },
        }),
        jobSave: builder.mutation<
            JobResponse,
            {data: FormData; jobId?: number}
        >({
            query: ({data, jobId}) => ({
                url:
                    typeof jobId === 'undefined'
                        ? 'jobs'
                        : `jobs/${jobId - 10000}`,
                method: typeof jobId === 'undefined' ? 'POST' : 'PUT',
                body: data,
            }),
            transformResponse: (response: JobResponse) => response,
            invalidatesTags: ['Job', 'JobCost', 'JobPackagingOptions'],
        }),
        getTotalProductCount: builder.query<number, number>({
            query: (jobId) => `jobs/${jobId - 10000}/total-product-count`,
            transformResponse: (response: {total_product_count: number}) =>
                response.total_product_count,
            providesTags: (response, error, jobId) => [
                {type: 'TotalProductCount', jobId},
            ],
        }),
        getJobDetailsPreview: builder.query<JobDetailsPreview, {jobId: number}>(
            {
                query: ({jobId}) => `job/${jobId}/preview/details`,
                providesTags: (response, error, {jobId}) => [
                    {type: 'JobDetailsPreview', jobId},
                ],
            }
        ),
        getJobPdfStatus: builder.query<
            {isJobQueued: number},
            {jobId: number; timestamp: number}
        >({
            query: ({jobId}) => `job/${jobId}/pdf/status`,
        }),
        downloadJobPDF: builder.query<
            {blob: Blob; headers: Headers},
            {id: number}
        >({
            query: ({id}) => {
                return {
                    url: `job/${id}/pdf/details`,
                    cache: 'no-cache',
                    responseHandler: async (response) => {
                        const blob = await response.blob();
                        return {blob, headers: response.headers};
                    },
                };
            },
        }),
        deleteFileUpload: builder.mutation<
            {success: boolean},
            {filename: string; jobId: number}
        >({
            query: ({filename, jobId}) => ({
                url: `jobs/${jobId}/file/${encodeURIComponent(filename)}`,
                method: 'DELETE',
            }),
        }),
    }),
});

// remove this once we properly implement rtk query to invalidate tags
export const invalidateJobCost = () => JobApi.util.invalidateTags(['JobCost']);
export const invalidateJob = () => JobApi.util.invalidateTags(['Job']);
export const invalidateVariations = () =>
    JobApi.util.invalidateTags(['Job', 'Variations', 'BTMBenches', 'JobCost']);
export const invalidateTotalProductCount = () =>
    JobApi.util.invalidateTags(['TotalProductCount']);
export const invalidateJobs = () => JobApi.util.invalidateTags(['Jobs']);

export const {
    useGetJobCostQuery,
    useLazyGetJobCostQuery,
    useJobResetMutation,
    useConfirmVariationsMutation,
    useGetJobStatusStatisticsQuery,
    useGetExtraVariationsQuery,
    useLazyGetExtraVariationsQuery,
    useUpdateRequestedDeliveryDateMutation,
    useLazyGetJobQuery,
    useGetJobQuery,
    useGetJobCabinetVariationDetailsQuery,
    useLazyGetJobCabinetVariationDetailsQuery,
    useLazyGetFingerPullRecessedRailsQuery,
    useJobSaveMutation,
    useGetTotalProductCountQuery,
    useGetJobDetailsPreviewQuery,
    useGetJobPdfStatusQuery,
    useLazyDownloadJobPDFQuery,
    useDeleteAllVariationsMutation,
    useDeleteFileUploadMutation,
    useGetJobsQuery,
} = JobApi;
